// Login.tsx
import { UnifiedHeader } from '@/components/UnifiedHeader';
import { fetchWithAuth } from '@/util/apiClient';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';

import backdropMain from '@/assets/backdrop-main.png';

// =============================================================================
// RESPONSIVE SIZING SYSTEM
// =============================================================================
// Figma specs (max size): Container 552x409, inputs 476x52, button 476x38
// All values scale proportionally using CSS clamp() with container width as base
// Container uses 25vw as preferred value (reduced from 29vw for better screen proportions)
// Min values = max * 0.667 (scales down to ~318px width at smaller viewports)
// =============================================================================

// Container dimensions - 552:409 aspect ratio (1.35:1)
const CONTAINER_WIDTH = 'clamp(318px, 25vw, 480px)';
// Height = width * (409/552) = width * 0.741
const CONTAINER_HEIGHT = 'clamp(236px, calc(25vw * 0.741), 356px)';

// Horizontal padding: (552 - 476) / 2 = 38px each side
const PADDING_X = 'clamp(21px, calc(25vw * 0.069), 33px)'; // 38/552 = 6.9%
// Vertical padding
const PADDING_TOP = 'clamp(17px, calc(25vw * 0.054), 26px)'; // reduced from 40px
const PADDING_BOTTOM = 'clamp(14px, calc(25vw * 0.045), 22px)'; // small padding at bottom

// Font sizes
const TITLE_FONT_SIZE = 'clamp(21px, calc(25vw * 0.065), 31px)'; // 36px in Figma
const ERROR_FONT_SIZE = 'clamp(10px, calc(25vw * 0.025), 12px)'; // Increased min from 8px to 10px
const RESET_FONT_SIZE = 'clamp(12px, calc(25vw * 0.029), 14px)'; // Increased min from 9px to 12px
const BUTTON_FONT_SIZE = 'clamp(14px, calc(25vw * 0.032), 15px)'; // Increased min from 10px to 14px

// Element heights from Figma
const ERROR_HEIGHT = 'clamp(29px, calc(25vw * 0.091), 44px)'; // restored to 50px
const INPUT_HEIGHT = 'clamp(30px, calc(25vw * 0.094), 45px)'; // 52px in Figma
const BUTTON_HEIGHT = 'clamp(22px, calc(25vw * 0.069), 33px)'; // 38px in Figma

// Margins
const MARGIN_SM = 'clamp(4px, calc(25vw * 0.013), 6px)'; // ~7px (tiny bit more after password)
const MARGIN_MD = 'clamp(10px, calc(25vw * 0.029), 14px)'; // Increased min from 7px to 10px

/**
 * Attempt to log in by hitting /auth/token/ with email & password.
 * If it fails, we parse the error response and throw an appropriate error.
 */
async function loginUser({
  email,
  password,
}: {
  email: string;
  password: string;
}): Promise<void> {
  const response = await fetchWithAuth('/auth/token/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    let errorMessage =
      'Something went wrong. Please try again, or contact support if the problem continues.';

    try {
      const errorData = await response.json();

      // Handle validation errors (400 status)
      if (response.status === 400 && errorData) {
        // Extract field-specific errors
        const fieldErrors: string[] = [];

        if (errorData.email && Array.isArray(errorData.email)) {
          fieldErrors.push(...errorData.email);
        }
        if (errorData.password && Array.isArray(errorData.password)) {
          fieldErrors.push(...errorData.password);
        }
        if (
          errorData.nonFieldErrors &&
          Array.isArray(errorData.nonFieldErrors)
        ) {
          fieldErrors.push(...errorData.nonFieldErrors);
        }

        if (fieldErrors.length > 0) {
          errorMessage = fieldErrors.join(' ');
        }
      }
      // Handle authentication failures (401 status)
      else if (response.status === 401) {
        errorMessage =
          "The email or password you entered doesn't match our records. Please try again.";
      }
      // Handle other error structures
      else if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // If JSON parsing fails, use a generic message based on status
      if (response.status === 401) {
        errorMessage =
          "The email or password you entered doesn't match our records. Please try again.";
      } else if (response.status === 400) {
        errorMessage = 'Please check your email and password and try again.';
      }
    }

    throw new Error(errorMessage);
  }
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  /**
   * React Query mutation to perform the login call.
   * On success, we wait a bit for cookies to arrive, then refreshAuth,
   * and navigate to "/".
   */
  const { mutate: doLogin, status } = useMutation<
    void,
    Error,
    { email: string; password: string }
  >({
    mutationFn: loginUser,
    onSuccess: async () => {
      // Wait briefly for cookies
      await new Promise((resolve) => setTimeout(resolve, 200));
      await auth.refreshAuth();
      navigate('/');
    },
    onError: (err) => {
      setError(err?.message || 'Login failed');
    },
  });

  // React Query v4 uses 'pending' to indicate the mutation is in progress
  const loggingIn = status === 'pending';

  function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    doLogin({ email, password });
  }

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#003D36',
      borderRadius: 0,
      fontFamily: '"Inter", sans-serif',
      '& fieldset': {
        border: 'none',
        borderBottom: '2px solid #7BBCB3',
      },
      '&:hover fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
      '&.Mui-focused fieldset': {
        borderBottom: '2px solid #7BBCB3',
      },
    },
    '& .MuiOutlinedInput-input': {
      color: '#ffffff',
      fontFamily: '"Inter", sans-serif',
      '&::placeholder': {
        color: '#7BBCB3',
        opacity: 1,
        fontWeight: 600,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#7BBCB3',
      fontFamily: '"Inter", sans-serif',
      '&.Mui-focused': {
        color: '#7BBCB3',
      },
    },
  };

  return (
    <>
      {/* Fixed background image */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${backdropMain})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
        }}
      />
      <UnifiedHeader
        hideProfileButton
        contactOpen={contactOpen}
        onContactToggle={() => setContactOpen(!contactOpen)}
      />
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            width: CONTAINER_WIDTH,
            maxWidth: 'calc(100vw - 96px)',
            pointerEvents: 'auto',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(14, 70, 63, 1)',
              borderRadius: 2,
              paddingLeft: PADDING_X,
              paddingRight: PADDING_X,
              paddingTop: PADDING_TOP,
              paddingBottom: PADDING_BOTTOM,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              height: CONTAINER_HEIGHT,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontFamily: '"Futura PT", "Futura", sans-serif',
                fontWeight: 700,
                fontSize: TITLE_FONT_SIZE,
                marginBottom: MARGIN_SM,
              }}
            >
              Login
            </Typography>

            <Box
              sx={{
                height: ERROR_HEIGHT,
                display: 'flex',
                alignItems: 'flex-start',
                mb: MARGIN_SM,
              }}
            >
              {error && (
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: ERROR_FONT_SIZE,
                    fontWeight: 600,
                    color: 'rgba(230, 174, 174, 1)',
                  }}
                >
                  *The email or password could not be found. Please{' '}
                  <Box
                    component='span'
                    onClick={() => setContactOpen(true)}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    contact CSI if you are having issues
                  </Box>
                  .
                </Typography>
              )}
            </Box>

            <Box
              component={'form'}
              onSubmit={handleLogin}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              <TextField
                fullWidth
                placeholder='Email'
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                slotProps={{
                  input: {
                    autoComplete: 'email',
                  },
                }}
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_MD,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />

              <TextField
                fullWidth
                placeholder='Password'
                type={'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                slotProps={{
                  input: {
                    autoComplete: 'current-password',
                  },
                }}
                sx={{
                  ...inputStyles,
                  marginBottom: MARGIN_SM,
                  '& .MuiOutlinedInput-root': {
                    ...inputStyles['& .MuiOutlinedInput-root'],
                    height: INPUT_HEIGHT,
                    minHeight: INPUT_HEIGHT,
                    boxSizing: 'border-box',
                  },
                  '& .MuiOutlinedInput-input': {
                    ...inputStyles['& .MuiOutlinedInput-input'],
                    height: '100%',
                    boxSizing: 'border-box',
                    padding: '0 14px',
                  },
                }}
              />

              <Typography
                component={Link}
                to='/reset-password'
                sx={{
                  textDecoration: 'none',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 600,
                  fontSize: RESET_FONT_SIZE,
                  color: 'rgba(43, 150, 122, 0.68)',
                  cursor: 'pointer',
                  textAlign: 'center',
                  display: 'block',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Reset Password
              </Typography>

              <Box sx={{ marginTop: 'auto' }}>
                <Button
                  fullWidth
                  variant='contained'
                  type='submit'
                  disabled={loggingIn}
                  disableRipple
                  sx={{
                    backgroundColor: 'rgba(9, 126, 113, 1) !important',
                    color: '#ffffff !important',
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    height: BUTTON_HEIGHT,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 'none',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: '#0a9485 !important',
                      boxShadow: 'none',
                    },
                    '&:active': {
                      backgroundColor: '#065a52 !important',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#4a7d78 !important',
                      color: 'rgba(255, 255, 255, 0.4) !important',
                    },
                  }}
                >
                  {loggingIn ? 'Logging in...' : 'Login'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
